/**
 * notification-display.ts — maps notification type/state_class to display
 * metadata (label, icon name, tone). Partner-generic: agent or LO.
 *
 * Suppressions never appear as bell rows (Plan A invariant; the backend
 * never emits a suppression as a notification type).
 */
import type { NotificationStateClass, NotificationActionKind } from "./api";
import notificationPolicy from "../../testdata/notification_policy.json";
import { PRO_ROUTE_PATHS } from "./routes";

type TypePresentation = {
  label: string;
  chip?: string;
  button?: string;
};

const TYPE_PRESENTATION = notificationPolicy.types as Record<
  string,
  TypePresentation
>;

// ─── Tone ────────────────────────────────────────────────────────────────────

/** Visual tone driven by state_class. Maps to @layer components atoms. */
export type NotificationTone = "failure" | "info" | "action";

export function toneForClass(
  stateClass: NotificationStateClass,
): NotificationTone {
  switch (stateClass) {
    case "failure":
      return "failure";
    case "action_required":
      return "action";
    case "info":
    default:
      return "info";
  }
}

// ─── Label ───────────────────────────────────────────────────────────────────

/** Human-readable label for the state-class chip inside each row. */
export function labelForClass(stateClass: NotificationStateClass): string {
  switch (stateClass) {
    case "failure":
      return "Needs attention";
    case "action_required":
      return "Action needed";
    case "info":
    default:
      return "FYI";
  }
}

// ─── Notification type → human label ─────────────────────────────────────────

/** One-line display label for a notification type. Partner-generic copy. */
export function labelForType(type: string): string {
  return TYPE_PRESENTATION[type]?.label ?? "Notification";
}

/** Per-type chip copy for the full log. Info rows intentionally have no chip. */
export function chipForType(type: string): string | undefined {
  return TYPE_PRESENTATION[type]?.chip;
}

/** Navigational CTA copy for the full log. Missing copy means row-tap only. */
export function buttonForType(type: string): string | undefined {
  return TYPE_PRESENTATION[type]?.button;
}

// ─── Action routing ───────────────────────────────────────────────────────────

/** Map an action kind + target_id to a pro-app route. */
export function routeForAction(
  kind: NotificationActionKind,
  targetId: string,
  marketplaceEnabled: boolean,
): string | null {
  switch (kind) {
    case "messages":
      // The messages workspace list. The target id is OPTIONAL: a safety
      // review can conclude on a pair that never had a thread (the consumer
      // twin's routeForActionKind carries the same note), so empty keeps the
      // bare list. The first-contact nudge carries the buyer's profile id,
      // which MessagesPage reads from `?client=` to open that conversation
      // (twin of the Go notificationActionURL).
      return targetId
        ? `${PRO_ROUTE_PATHS.messages}?client=${encodeURIComponent(targetId)}`
        : PRO_ROUTE_PATHS.messages;
    case "client_profile":
      return `/clients/${targetId}`;
    case "campaign":
      return PRO_ROUTE_PATHS.campaigns;
    case "automation":
      return PRO_ROUTE_PATHS.automations;
    case "contact":
      return `/contacts?contact=${targetId}`;
    case "draft":
      // The Tasks queue shows the draft; target_id is the draft/campaign id
      return `/tasks?draft=${targetId}`;
    case "listing":
      return `/clients?listing=${targetId}`;
    case "marketplace_offer":
      // The pro's own offers ledger, deep-linked to the offer the
      // notification is about (task:m5-actionkind-route). Never the
      // unmapped fail-closed default path.
      return marketplaceEnabled
        ? `/marketplace/offers?offer=${targetId}`
        : null;
    case "marketplace_billing":
      return "/settings#billing";
    case "marketplace_requests":
      // The pro's requests inbox (M6-B M9.9 remediation) — previously
      // unmapped, so a "New connection request" notification silently
      // fell through to the default instead of the requests inbox.
      return marketplaceEnabled ? `/marketplace/requests/${targetId}` : null;
    case "marketplace_counter":
      // The opaque window id is resolved and authorized by the compose
      // context endpoint; never place a profile UUID or competitor identity
      // in notification payloads or URLs.
      return marketplaceEnabled
        ? `/marketplace/browse/recovery?recovery_window_id=${encodeURIComponent(targetId)}`
        : null;
    case "marketplace_profile":
      // The pro's own marketplace supply profile (verification card + resubmit).
      return marketplaceEnabled ? "/marketplace/profile" : null;
  }
  const exhaustiveKind: never = kind;
  return exhaustiveKind;
}

export const NOTIFICATION_ACTION_KINDS = [
  "contact",
  "draft",
  "listing",
  "client_profile",
  "campaign",
  "automation",
  "marketplace_offer",
  "marketplace_billing",
  "marketplace_requests",
  "marketplace_counter",
  "marketplace_profile",
  "messages",
] as const satisfies readonly NotificationActionKind[];

/** Router paths reachable from notification actions (query/hash excluded). */
const ALWAYS_REGISTERED_ACTION_ROUTE_PATTERNS = [
  PRO_ROUTE_PATHS.clients,
  PRO_ROUTE_PATHS.clientDetail,
  PRO_ROUTE_PATHS.contacts,
  PRO_ROUTE_PATHS.home,
  PRO_ROUTE_PATHS.tasks,
  PRO_ROUTE_PATHS.settings,
  PRO_ROUTE_PATHS.messages,
  PRO_ROUTE_PATHS.campaigns,
  PRO_ROUTE_PATHS.automations,
] as const;

const MARKETPLACE_ACTION_ROUTE_PATTERNS = [
  PRO_ROUTE_PATHS.marketplaceOffers,
  PRO_ROUTE_PATHS.marketplaceBrowseDetail,
  PRO_ROUTE_PATHS.marketplaceBrowseRecovery,
  PRO_ROUTE_PATHS.marketplaceRequestDetail,
  PRO_ROUTE_PATHS.marketplaceProfile,
] as const;

export function routeMatchesRegisteredPattern(
  route: string | null,
  marketplaceEnabled: boolean,
): boolean {
  if (route === null) return false;

  const pathname = route.split(/[?#]/, 1)[0];
  const registeredPatterns = marketplaceEnabled
    ? [
        ...ALWAYS_REGISTERED_ACTION_ROUTE_PATTERNS,
        ...MARKETPLACE_ACTION_ROUTE_PATTERNS,
      ]
    : ALWAYS_REGISTERED_ACTION_ROUTE_PATTERNS;
  return registeredPatterns.some((pattern) => {
    const patternParts = pattern.split("/");
    const pathParts = pathname.split("/");
    return (
      patternParts.length === pathParts.length &&
      patternParts.every(
        (part, index) => part.startsWith(":") || part === pathParts[index],
      )
    );
  });
}
